import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, Package, MessageSquare, ShoppingBag } from 'lucide-react';
import { Button } from '../ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { notificationsAPI } from '../../lib/api';
import { cn } from '../../lib/utils';

export function NotificationBell() {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();

    const fetchNotifications = useCallback(async () => {
        try {
            const [notifRes, countRes] = await Promise.all([
                notificationsAPI.getAll(),
                notificationsAPI.getUnreadCount()
            ]);
            setNotifications(notifRes.data);
            setUnreadCount(countRes.data.count);
        } catch { /* ignore if not logged in */ }
    }, []);

    useEffect(() => {
        fetchNotifications();
        const interval = setInterval(fetchNotifications, 30000);
        return () => clearInterval(interval);
    }, [fetchNotifications]);

    const handleMarkRead = async (id) => {
        await notificationsAPI.markRead(id);
        setNotifications(prev => prev.map(n => n.id === id ? {...n, read: true} : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
    };

    const handleMarkAllRead = async () => {
        await notificationsAPI.markAllRead();
        setNotifications(prev => prev.map(n => ({...n, read: true})));
        setUnreadCount(0);
    };

    const handleClick = (notif) => {
        if (!notif.read) handleMarkRead(notif.id);
        if (notif.order_id) {
            if (notif.type === 'new_order') {
                navigate('/admin?tab=orders');
            } else {
                navigate('/dashboard?tab=orders');
            }
            setOpen(false);
        }
    };

    const getIcon = (type) => {
        if (type === 'support_reply') return <MessageSquare className="w-4 h-4 text-cyan-500" />;
        if (type === 'new_order') return <ShoppingBag className="w-4 h-4 text-green-500" />;
        return <Package className="w-4 h-4 text-cyan-500" />;
    };

    const timeAgo = (dateStr) => {
        const diff = Date.now() - new Date(dateStr).getTime();
        const mins = Math.floor(diff / 60000);
        if (mins < 1) return 'most';
        if (mins < 60) return `${mins} perce`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs} órája`;
        return `${Math.floor(hrs / 24)} napja`;
    };

    return (
        <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell">
                    <Bell className="h-4 w-4" />
                    {unreadCount > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-red-500 text-[10px] font-bold flex items-center justify-center text-white">
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                    )}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 max-h-[400px] overflow-y-auto p-0">
                <div className="flex items-center justify-between px-4 py-3 border-b">
                    <span className="font-semibold text-sm">Értesítések</span>
                    {unreadCount > 0 && (
                        <button
                            onClick={handleMarkAllRead}
                            className="text-xs text-cyan-500 hover:text-cyan-400 flex items-center gap-1"
                            data-testid="mark-all-read-btn"
                        >
                            <CheckCheck className="w-3 h-3" /> Mind olvasott
                        </button>
                    )}
                </div>
                {notifications.length === 0 ? (
                    <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                        Nincsenek értesítések
                    </div>
                ) : (
                    notifications.slice(0, 15).map(notif => (
                        <div
                            key={notif.id}
                            onClick={() => handleClick(notif)}
                            className={cn(
                                "flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-secondary/50 transition-colors border-b last:border-b-0",
                                !notif.read && "bg-cyan-500/5"
                            )}
                            data-testid={`notification-item-${notif.id}`}
                        >
                            <div className="mt-0.5">{getIcon(notif.type)}</div>
                            <div className="flex-1 min-w-0">
                                <p className={cn("text-sm leading-tight", !notif.read && "font-medium")}>{notif.title}</p>
                                <p className="text-xs text-muted-foreground mt-0.5 truncate">{notif.message}</p>
                                <p className="text-xs text-muted-foreground mt-1">{timeAgo(notif.created_at)}</p>
                            </div>
                            {!notif.read && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleMarkRead(notif.id); }}
                                    className="mt-1 p-1 rounded hover:bg-secondary"
                                >
                                    <Check className="w-3 h-3 text-muted-foreground" />
                                </button>
                            )}
                        </div>
                    ))
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
